/**
 * Favicon que acompanha o tema do navegador.
 *
 * Não existe API para ler a cor da barra de abas. O que dá para consultar é
 * `prefers-color-scheme` — a preferência de tema do sistema, que é o que faz a
 * aba ser clara ou escura. É o sinal correto para escolher entre o ícone azul
 * (aba clara) e o branco (aba escura).
 *
 * O `index.html` já declara os dois ícones com `media`, o que resolve sozinho
 * no Safari e no Chrome. Este módulo existe porque o Firefox ignora `media` em
 * `<link rel="icon">`, e porque trocar de tema com a aba aberta não reavalia a
 * declaração — sem o listener, o ícone só corrigiria no próximo carregamento.
 */

const ICONES = {
  azul: '/assets/favicon-azul.ico',
  branco: '/assets/favicon-branco.ico',
} as const;

export type VarianteFavicon = keyof typeof ICONES;

/** `auto` acompanha o tema; as demais fixam o ícone, para estados do sistema. */
export type ModoFavicon = 'auto' | VarianteFavicon;

const CONSULTA_ESCURO = '(prefers-color-scheme: dark)';

export type Tema = 'claro' | 'escuro';

export function temaDoNavegador(): Tema {
  // matchMedia não existe em ambiente sem DOM; a guarda evita quebrar em teste
  // ou em qualquer render fora do navegador.
  if (typeof window === 'undefined' || !window.matchMedia) return 'claro';
  return window.matchMedia(CONSULTA_ESCURO).matches ? 'escuro' : 'claro';
}

/** Aba escura pede o ícone branco; aba clara, o azul. */
export function varianteDoTema(tema: Tema = temaDoNavegador()): VarianteFavicon {
  return tema === 'escuro' ? 'branco' : 'azul';
}

/** Avisa quando o usuário troca o tema com a aba já aberta. */
export function observarTema(aoMudar: (tema: Tema) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;

  const consulta = window.matchMedia(CONSULTA_ESCURO);
  const handler = (evento: MediaQueryListEvent) => {
    aoMudar(evento.matches ? 'escuro' : 'claro');
  };

  consulta.addEventListener('change', handler);
  return () => {
    consulta.removeEventListener('change', handler);
  };
}

let atual: string | null = null;

/**
 * Troca o ícone da aba.
 *
 * Substitui o elemento inteiro em vez de reatribuir o `href`: os navegadores
 * cacheiam o favicon de forma agressiva e, com o mesmo elemento, boa parte
 * deles simplesmente ignora a troca.
 */
export function aplicarFavicon(variante: VarianteFavicon): void {
  if (typeof document === 'undefined') return;

  const href = ICONES[variante];
  if (href === atual) return;
  atual = href;

  document.querySelectorAll('link[rel~="icon"]').forEach((link) => {
    link.remove();
  });

  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Liga o favicon ao tema do navegador e mantém ligado.
 *
 * Devolve a função de parar, para quem precisar assumir o controle do ícone.
 */
export function iniciarFaviconAdaptativo(): () => void {
  aplicarFavicon(varianteDoTema());
  return observarTema((tema) => {
    aplicarFavicon(varianteDoTema(tema));
  });
}
